import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { z } from "zod";

export type ConfigBand = OrgConfig.ExternalCapacity["seatBands"][number];

export const BAND_COLOR_SCHEMES = [
  "green",
  "teal",
  "blue",
  "purple",
  "yellow",
  "orange",
  "red",
  "gray",
] as const;

export const BandFormikSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1, "This field is required"),
  colorScheme: z.enum(BAND_COLOR_SCHEMES),
  minCount: z.number().int().min(0, "Must be 0 or more"),
});

export type BandFormik = z.infer<typeof BandFormikSchema>;

/**
 * Bands are matched from the highest threshold down and the lowest band is the
 * catch-all, so a list without a zero-threshold band would leave the most
 * constrained schools with nothing to display.
 */
export function hasCatchAllBand(bands: BandFormik[]): boolean {
  return bands.some((band) => band.minCount === 0);
}

function sortedByThresholdDesc(bands: BandFormik[]): BandFormik[] {
  return [...bands].sort((a, b) => b.minCount - a.minCount);
}

export function newBand(): BandFormik {
  return {
    key: globalThis.crypto.randomUUID(),
    label: "",
    colorScheme: "gray",
    minCount: 0,
  };
}

export function toConfigBands(bands: BandFormik[]): ConfigBand[] {
  return sortedByThresholdDesc(bands).map((band) => ({
    key: band.key,
    label: band.label.trim(),
    colorScheme: band.colorScheme,
    minCount: band.minCount,
  }));
}

export function fromConfigBands(bands: ConfigBand[]): BandFormik[] {
  return sortedByThresholdDesc(
    bands.map((band) => ({
      key: band.key,
      label: band.label,
      colorScheme: band.colorScheme,
      minCount: band.minCount,
    }))
  );
}
